import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Star, Heart, MessageCircle, Trash2, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import BottomNav from "../components/BottomNav";
import EmptyState from "../components/EmptyState";

import { API } from "../config";
import { tx } from "../lib/tx";

export default function Favorites() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/favorites`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (userId) => {
    setRemoving(userId);
    try {
      const token = localStorage.getItem("session_token");
      const response = await fetch(`${API}/favorites/${userId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        setFavorites(prev => prev.filter(f => f.user_id !== userId));
      }
    } catch (error) {
      console.error("Error removing favorite:", error);
    } finally {
      setRemoving(null);
    }
  };

  const getPhotoUrl = (user) => {
    if (user.main_photo) {
      if (user.main_photo.startsWith('http')) return user.main_photo;
      return `${API}${user.main_photo.replace('/api', '')}`;
    }
    return user.photos?.[0] || user.picture || '/default-avatar.png';
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5 p-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-white"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="flex items-center gap-2">
            <Star className="w-6 h-6 text-yellow-500" />
            <h1 className="font-heading text-xl font-bold text-white">{tx("Mes Favoris")}</h1>
          </div>
          {favorites.length > 0 && (
            <span className="ml-auto bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-semibold">
              {favorites.length}
            </span>
          )}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : favorites.length === 0 ? (
          <EmptyState 
            type="noFavorites" 
            language="fr"
            action={() => navigate('/discover')}
            actionLabel={tx("Découvrir des profils")}
          />
        ) : (
          <div className="space-y-4">
            {favorites.map((fav, index) => (
              <motion.div
                key={fav.user_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-card/50 border border-white/10 rounded-2xl p-4 flex items-center gap-4"
              >
                {/* Photo */}
                <div 
                  onClick={() => navigate(`/user/${fav.user_id}`)}
                  className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer"
                >
                  <img
                    src={getPhotoUrl(fav)}
                    alt={fav.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 
                    onClick={() => navigate(`/user/${fav.user_id}`)}
                    className="font-semibold text-white text-lg truncate cursor-pointer hover:text-primary"
                  >
                    {fav.name}, {fav.age}
                  </h3>
                  {fav.city && (
                    <p className="text-sm text-muted-foreground truncate">{fav.city}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {tx("Ajouté le")} {new Date(fav.favorited_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(`/chat/${fav.user_id}`)}
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFavorite(fav.user_id)}
                    disabled={removing === fav.user_id}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    {removing === fav.user_id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Trash2 className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
